import React, { useMemo, Fragment } from "react";

import type { FieldId } from "src/types";
import type {
  AiChecklistSuggestion,
  AiSuggestionSelectionMatrix,
  AiFlatSuggestionValue,
  AiLinkedFieldSuggestionValues,
} from "src/ts-types";

import Link from "./FieldCell/Link";
import Picklist from "./FieldCell/Picklist";
import { formatFieldValue } from "src/utils/checklist";

type Props = {
  fieldId: FieldId;
  fieldType: string;
  isDisabled: boolean;
  suggestion: AiChecklistSuggestion;
  toggleSuggestion: (
    fieldId: FieldId,
    fieldSuggestionIndex: number,
    embeddedSuggestionIndex?: number,
  ) => void;
  isSuggestionSelected: (
    fieldId: FieldId,
    fieldSuggestionIndex: number,
    embeddedSuggestionIndex?: number,
  ) => boolean;
  selectionSuggestions: AiSuggestionSelectionMatrix;
};

// Utility function to strip html tags from a string
const stripHtmlTags = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.body.textContent || doc.body.innerText || "";
};

const TableCell = ({
  fieldId,
  fieldType,
  isDisabled,
  suggestion,
  toggleSuggestion,
  isSuggestionSelected,
  selectionSuggestions,
}: Props) => {
  const genericValue = useMemo(
    () =>
      (Array.isArray(suggestion.value)
        ? suggestion.value
        : [suggestion.value]
      ).map((val, index) => {
        // For richtext, format the value using the same utility as checklist fields
        if (fieldType === "richtext") {
          const formatted = formatFieldValue(
            fieldType,
            typeof val === "string"
              ? { htmlText: val, plainText: stripHtmlTags(val) }
              : val,
          );
          return <Fragment key={`${fieldId}-${index}`}>{formatted}</Fragment>;
        }
        return (
          <Fragment key={`${fieldId}-${index}`}>
            {formatFieldValue(fieldType, val) ?? String(val)}
          </Fragment>
        );
      }),
    [suggestion, fieldId, fieldType],
  );

  if (!fieldId) return null;

  // We're trusting the value payload to be valid for the given field type.
  switch (fieldType) {
    case "text":
    case "date":
    case "richtext":
    case "number":
      return genericValue;
    case "link":
      return (
        <Link
          fieldId={fieldId}
          isDisabled={isDisabled}
          value={suggestion.value as AiLinkedFieldSuggestionValues}
          toggleSuggestion={toggleSuggestion}
          isSuggestionSelected={isSuggestionSelected}
          selectionSuggestions={selectionSuggestions}
        />
      );
    case "picklist":
      return (
        <Picklist
          fieldId={fieldId}
          isDisabled={isDisabled}
          value={suggestion.value as AiFlatSuggestionValue}
          toggleSuggestion={toggleSuggestion}
          isSuggestionSelected={isSuggestionSelected}
        />
      );
    default:
      return "Unsupported value";
  }
};

export default TableCell;
